// ✅ IMPROVED FIX: ConsignmentEntryFormLT.jsx
// Now shows "Loading..." in the input field during generation
// Better async handling to ensure number appears

import React, { useState, useEffect } from 'react';
import { X, Package, User, Truck, FileText, Save, Search, Plus, MapPin, Printer } from 'lucide-react';
import '../styles/ConsignmentEntryFormLT.css';
import apiClient from '../services/api';
import { printConsignment } from '../utils/printUtils';

const ConsignmentEntryFormLT = ({ onClose, onSave, editData = null }) => {
  const CONSIGNMENT_TYPE = 'LT'; // Land Transport
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false); // NEW: Track generation state
  
  // Customer Selection Modal States
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerModalType, setCustomerModalType] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  
  // Get session data
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  
  const [formData, setFormData] = useState({
    ConsignmentNo: '',
    ConsignmentDate: new Date().toISOString().slice(0, 16),
    ConsignmentType: CONSIGNMENT_TYPE,
    
    // Consignment Route
    OriginCityID: '',
    DestinationCityID: '',
    
    // Shipper
    ShipperID: '',
    ShipperCode: '',
    ShipperName: '',
    ShipperContactPerson: '',
    ShipperFlatBuilding: '',
    ShipperStreet: '',
    ShipperCustomerIDNo: '',
    ShipperPhone: '',
    ShipperWhatsApp: '',
    ShipperCountryID: '',
    ShipperStateID: '',
    ShipperCityID: '',
    
    // Consignee
    ConsigneeID: '',
    ConsigneeCode: '',
    ConsigneeName: '',
    ConsigneeContactPerson: '',
    ConsigneeFlatBuilding: '',
    ConsigneeStreet: '',
    ConsigneePhone: '',
    ConsigneeWhatsApp: '',
    ConsigneeCountryID: '',
    ConsigneeStateID: '',
    ConsigneeCityID: '',
    
    // Package Details
    Description: '',
    NoOfPkgs: '',
    Weight: '',
    Pieces: '',
    Volume: '',
    VolumeWeight: '',
    InvoiceValue: '',
    CourierType: '',
    PaymentMode: '',
    ProductType: '',
    
    // Transport & Collection
    DespatchDate: '',
    VehicleID: '',
    DriverID: '',
    CollectedBy: '',
    ReceivedBy: '',
    Load: 'FTL',
    
    // Delivery
    CourierStatusID: '',
    DeliveryDate: '',
    
    // Special Instructions
    NCND: false,
    DDCopyBack: false,
    CashOnly: false,
    ChequeCash: false,
    CollectMaterial: false,
    MaterialCost: '',
    Reference: '',
    Remarks: '',
    
    // Other
    Transit: false,
    SeparateDocs: false
  });

  const [newCustomerData, setNewCustomerData] = useState({
    customername: '',
    customertype: 'CR',
    phone: '',
    whatsappno: '',
    email: '',
    address: '',
    countryid: '',
    stateid: '',
    cityid: '',
    taxnumber: ''
  });

  // States for cascading dropdowns
  const [shipperStates, setShipperStates] = useState([]);
  const [shipperCities, setShipperCities] = useState([]);
  const [consigneeStates, setConsigneeStates] = useState([]);
  const [consigneeCities, setConsigneeCities] = useState([]);
  const [newCustomerStates, setNewCustomerStates] = useState([]);
  const [newCustomerCities, setNewCustomerCities] = useState([]);

  // ✅ IMPROVED: Initialize form with better async handling
  useEffect(() => {
    const initializeForm = async () => {
      console.log('🔧 Initializing form...');
      console.log('📊 Edit data:', editData);
      
      // Load dropdown data first
      await loadDropdownData();
      
      if (editData && editData.ConsignmentID) {
        // EDITING MODE: Load existing data
        console.log('📝 Edit mode: Loading data for', editData.ConsignmentNo);
        setFormData(prev => ({
          ...prev,
          ...editData,
          ConsignmentType: CONSIGNMENT_TYPE,
          ConsignmentDate: editData.ConsignmentDate || new Date().toISOString().slice(0, 16),
          ConsignmentNo: editData.ConsignmentNo || '',
          ShipperName: editData.ShipperName || '',
          ConsigneeName: editData.ConsigneeName || ''
        }));
      } else {
        // NEW MODE: Generate consignment number
        console.log('➕ New mode: Generating consignment number');
        setGeneratingNumber(true);
        await generateConsignmentNumber();
        setGeneratingNumber(false);
      }
    };
    
    initializeForm();
  }, []); // Run once on mount
  
  // Load states when shipper country changes
  useEffect(() => {
    if (formData.ShipperCountryID) {
      loadStatesByCountry(formData.ShipperCountryID, 'shipper');
    } else {
      setShipperStates([]);
      setShipperCities([]);
    }
  }, [formData.ShipperCountryID]);

  // Load cities when shipper state changes
  useEffect(() => {
    if (formData.ShipperStateID) {
      loadCitiesByState(formData.ShipperStateID, 'shipper');
    } else {
      setShipperCities([]);
    }
  }, [formData.ShipperStateID]);

  // Load states when consignee country changes
  useEffect(() => {
    if (formData.ConsigneeCountryID) {
      loadStatesByCountry(formData.ConsigneeCountryID, 'consignee');
    } else {
      setConsigneeStates([]);
      setConsigneeCities([]);
    }
  }, [formData.ConsigneeCountryID]);

  // Load cities when consignee state changes
  useEffect(() => {
    if (formData.ConsigneeStateID) {
      loadCitiesByState(formData.ConsigneeStateID, 'consignee');
    } else {
      setConsigneeCities([]);
    }
  }, [formData.ConsigneeStateID]);

  const loadDropdownData = async () => {
    try {
      console.log('📥 Loading dropdown data...');
      // Load all required data
      const [empRes, driverRes, custRes, vehRes, cityRes, countryRes, stateRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Customers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Vehicles.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Countries.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/States.php').catch(e => ({ data: { success: false } }))
      ]);

      // Set data from responses
      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (custRes.data.success) setCustomers(custRes.data.data || []);
      if (vehRes.data.success) setVehicles(vehRes.data.data || []);
      if (cityRes.data.success) setCities(cityRes.data.data || []);
      if (countryRes.data.success) setCountries(countryRes.data.data || []);
      if (stateRes.data.success) setStates(stateRes.data.data || []);
      
      // ✅ USE DRIVERS TABLE - Filter active drivers only
      if (driverRes.data.success) {
        const allDrivers = driverRes.data.data || [];
        const activeDrivers = allDrivers.filter(d => d.isactive === 1);
        setDrivers(activeDrivers);
        console.log('✅ Loaded', activeDrivers.length, 'active drivers');
      } else {
        console.warn('⚠️ Drivers API not available');
        setDrivers([]);
      }
      
      console.log('✅ Dropdown data loaded successfully');
    } catch (err) {
      console.error('❌ Error loading dropdown data:', err);
      setError('Failed to load dropdown data');
    }
  };

  const loadStatesByCountry = async (countryId, type) => {
    try {
      const response = await apiClient.get('/States.php', {
        params: { countryid: countryId }
      });
      if (response.data.success) {
        if (type === 'shipper') {
          setShipperStates(response.data.data || []);
        } else if (type === 'consignee') {
          setConsigneeStates(response.data.data || []);
        } else {
          setNewCustomerStates(response.data.data || []);
        }
      }
    } catch (err) {
      console.error('Error loading states:', err);
    }
  };

  const loadCitiesByState = async (stateId, type) => {
    try {
      const response = await apiClient.get('/Cities.php', {
        params: { stateid: stateId }
      });
      if (response.data.success) {
        if (type === 'shipper') {
          setShipperCities(response.data.data || []);
        } else if (type === 'consignee') {
          setConsigneeCities(response.data.data || []);
        } else {
          setNewCustomerCities(response.data.data || []);
        }
      }
    } catch (err) {
      console.error('Error loading cities:', err);
    }
  };

  // ✅ IMPROVED: Generate consignment number with explicit state updates
  const generateConsignmentNumber = async () => {
    try {
      console.log('🔢 Starting consignment number generation...');
      const response = await apiClient.get(`/ConsignmentsLT.php/next-number?type=${CONSIGNMENT_TYPE}`);
      
      console.log('📦 API Response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.consignmentNo) {
        const newNumber = response.data.data.consignmentNo;
        console.log('✅ Generated number:', newNumber);
        
        // Update state with the new number
        setFormData(prev => {
          const updated = {
            ...prev,
            ConsignmentNo: newNumber
          };
          console.log('📝 Form data updated with number:', updated.ConsignmentNo);
          return updated;
        });
        
        console.log('✅ Consignment number generation complete');
        return newNumber;
      } else {
        console.error('❌ API returned invalid response:', response.data);
        throw new Error('Failed to generate consignment number');
      }
    } catch (err) {
      console.error('❌ Error generating number:', err);
      
      // Generate fallback number
      const timestamp = Date.now();
      const fallbackNumber = `CNS-LT-${timestamp}`;
      console.log('⚠️ Using fallback number:', fallbackNumber);
      
      setFormData(prev => ({ 
        ...prev, 
        ConsignmentNo: fallbackNumber 
      }));
      
      return fallbackNumber;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShipperCountryChange = (countryId) => {
    setFormData(prev => ({
      ...prev,
      ShipperCountryID: countryId,
      ShipperStateID: '',
      ShipperCityID: ''
    }));
  };

  const handleShipperStateChange = (stateId) => {
    setFormData(prev => ({
      ...prev,
      ShipperStateID: stateId,
      ShipperCityID: ''
    }));
  };

  const handleConsigneeCountryChange = (countryId) => {
    setFormData(prev => ({
      ...prev,
      ConsigneeCountryID: countryId,
      ConsigneeStateID: '',
      ConsigneeCityID: ''
    }));
  };

  const handleConsigneeStateChange = (stateId) => {
    setFormData(prev => ({
      ...prev,
      ConsigneeStateID: stateId,
      ConsigneeCityID: ''
    }));
  };

  const openCustomerModal = (type) => {
    setCustomerModalType(type);
    setShowCustomerModal(true);
    setCustomerSearchTerm('');
  };

  const selectCustomer = async (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(prev => ({
        ...prev,
        ShipperID: customer.customerid,
        ShipperCode: customer.customercode,
        ShipperName: customer.customername,
        ShipperPhone: customer.phone || '',
        ShipperWhatsApp: customer.whatsappno || '',
        ShipperCountryID: customer.countryid || '',
        ShipperStateID: customer.stateid || '',
        ShipperCityID: customer.cityid || ''
      }));
      
      if (customer.countryid) {
        await loadStatesByCountry(customer.countryid, 'shipper');
      }
      if (customer.stateid) {
        await loadCitiesByState(customer.stateid, 'shipper');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        ConsigneeID: customer.customerid,
        ConsigneeCode: customer.customercode,
        ConsigneeName: customer.customername,
        ConsigneePhone: customer.phone || '',
        ConsigneeWhatsApp: customer.whatsappno || '',
        ConsigneeCountryID: customer.countryid || '',
        ConsigneeStateID: customer.stateid || '',
        ConsigneeCityID: customer.cityid || ''
      }));
      
      if (customer.countryid) {
        await loadStatesByCountry(customer.countryid, 'consignee');
      }
      if (customer.stateid) {
        await loadCitiesByState(customer.stateid, 'consignee');
      }
    }
    setShowCustomerModal(false);
  };

  const handleNewCustomerCountryChange = async (countryId) => {
    setNewCustomerData(prev => ({
      ...prev,
      countryid: countryId,
      stateid: '',
      cityid: ''
    }));
    
    if (countryId) {
      await loadStatesByCountry(countryId, 'newcustomer');
    } else {
      setNewCustomerStates([]);
      setNewCustomerCities([]);
    }
  };

  const handleNewCustomerStateChange = async (stateId) => {
    setNewCustomerData(prev => ({
      ...prev,
      stateid: stateId,
      cityid: ''
    }));
    
    if (stateId) {
      await loadCitiesByState(stateId, 'newcustomer');
    } else {
      setNewCustomerCities([]);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerData.customername.trim()) {
      alert('Customer name is required');
      return;
    }

    try {
      const response = await apiClient.post('/Customers.php', {
        ...newCustomerData,
        companyid: companyId,
        isactive: 1
      });
      
      if (response.data.success) {
        await loadDropdownData();
        
        const newCustomer = {
          customerid: response.data.data.customerid,
          customercode: response.data.data.customercode,
          customername: newCustomerData.customername,
          phone: newCustomerData.phone,
          whatsappno: newCustomerData.whatsappno,
          countryid: newCustomerData.countryid,
          stateid: newCustomerData.stateid,
          cityid: newCustomerData.cityid
        };
        
        selectCustomer(newCustomer);
        
        setNewCustomerData({
          customername: '',
          customertype: 'CR',
          phone: '',
          whatsappno: '',
          email: '',
          address: '',
          countryid: '',
          stateid: '',
          cityid: '',
          taxnumber: ''
        });
        setNewCustomerStates([]);
        setNewCustomerCities([]);
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${response.data.data.customercode}`);
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error adding customer:', err);
      alert('Error adding customer: ' + (err.response?.data?.message || err.message));
    }
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!(formData.ConsignmentNo || '').trim()) {
      setError('Consignment number is required');
      return;
    }
    
    if (!(formData.ShipperName || '').trim()) {
      setError('Shipper name is required');
      return;
    }
    
    if (!(formData.ConsigneeName || '').trim()) {
      setError('Consignee name is required');
      return;
    }
    
    if (!formData.OriginCityID) {
      setError('Origin city is required');
      return;
    }
    
    if (!formData.DestinationCityID) {
      setError('Destination city is required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ConsignmentNo: formData.ConsignmentNo,
        ConsignmentDate: formData.ConsignmentDate,
        ConsignmentType: 'LT',
        
        // Route
        OriginCityID: formData.OriginCityID || null,
        DestinationCityID: formData.DestinationCityID || null,
        
        // Shipper
        ShipperID: formData.ShipperID || null,
        ShipperCode: formData.ShipperCode || '',
        ShipperName: formData.ShipperName || '',
        ShipperContactPerson: formData.ShipperContactPerson || '',
        ShipperFlatBuilding: formData.ShipperFlatBuilding || '',
        ShipperStreet: formData.ShipperStreet || '',
        ShipperCustomerIDNo: formData.ShipperCustomerIDNo || '',
        ShipperPhone: formData.ShipperPhone || '',
        ShipperWhatsApp: formData.ShipperWhatsApp || '',
        ShipperCountryID: formData.ShipperCountryID || null,
        ShipperStateID: formData.ShipperStateID || null,
        ShipperCityID: formData.ShipperCityID || null,
        
        // Consignee
        ConsigneeID: formData.ConsigneeID || null,
        ConsigneeCode: formData.ConsigneeCode || '',
        ConsigneeName: formData.ConsigneeName || '',
        ConsigneeContactPerson: formData.ConsigneeContactPerson || '',
        ConsigneeFlatBuilding: formData.ConsigneeFlatBuilding || '',
        ConsigneeStreet: formData.ConsigneeStreet || '',
        ConsigneePhone: formData.ConsigneePhone || '',
        ConsigneeWhatsApp: formData.ConsigneeWhatsApp || '',
        ConsigneeCountryID: formData.ConsigneeCountryID || null,
        ConsigneeStateID: formData.ConsigneeStateID || null,
        ConsigneeCityID: formData.ConsigneeCityID || null,
        
        // Package Details
        Description: formData.Description || '',
        NoOfPkgs: formData.NoOfPkgs || 0,
        Weight: formData.Weight || 0,
        Pieces: formData.Pieces || 0,
        Volume: formData.Volume || 0,
        VolumeWeight: formData.VolumeWeight || 0,
        InvoiceValue: formData.InvoiceValue || 0,
        CourierType: formData.CourierType || '',
        PaymentMode: formData.PaymentMode || '',
        ProductType: formData.ProductType || '',
        
        // Transport & Collection
        DespatchDate: formData.DespatchDate || '',
        VehicleID: formData.VehicleID || null,
        DriverID: formData.DriverID || null,
        CollectedBy: formData.CollectedBy || null,
        ReceivedBy: formData.ReceivedBy || null,
        Load: formData.Load || 'FTL',
        
        // Delivery
        CourierStatusID: formData.CourierStatusID || null,
        DeliveryDate: formData.DeliveryDate || '',
        
        // Special Instructions
        NCND: formData.NCND ? 1 : 0,
        DDCopyBack: formData.DDCopyBack ? 1 : 0,
        CashOnly: formData.CashOnly ? 1 : 0,
        ChequeCash: formData.ChequeCash ? 1 : 0,
        CollectMaterial: formData.CollectMaterial ? 1 : 0,
        MaterialCost: formData.MaterialCost || 0,
        Reference: formData.Reference || '',
        Remarks: formData.Remarks || '',
        
        // Other
        Transit: formData.Transit ? 1 : 0,
        SeparateDocs: formData.SeparateDocs ? 1 : 0
      };
      
      // If editing, include ConsignmentID
      if (editData && editData.ConsignmentID) {
        dataToSave.ConsignmentID = editData.ConsignmentID;
      }
      
      // Delegate to parent
      await onSave(dataToSave);
      
    } catch (err) {
      setError(err.message || 'Failed to save consignment');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  // Check session
  if (!companyId) {
    return (
      <div className="consignment-form-overlay">
        <div className="consignment-form-container" style={{ maxWidth: '600px' }}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '16px' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consignment-form-overlay">
      <div className="consignment-form-container" style={{ maxWidth: '1400px' }}>
        {/* ✅ CLEAN WHITE TEXT HEADER - All inline styles */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '28px 32px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '4px solid #5a67d8',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <Truck size={24} color="#ffffff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div style={{ 
                  color: '#ffffff',
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                  {editData ? 'Edit' : 'New'} Consignment - Land Transport (LT)
                </div>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: 'rgba(255,255,255,0.25)',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  display: 'inline-block',
                  border: '1px solid rgba(255,255,255,0.4)',
                  fontFamily: '"Courier New", monospace'
                }}>
                  📋 {generatingNumber ? 'Generating...' : (formData.ConsignmentNo || 'Loading...')}
                </div>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <X size={20} color="#ffffff" />
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-content">
            
            {/* Basic Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <FileText size={18} />
                Basic Information (Land Transport)
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Consignment No *</label>
                  <input
                    type="text"
                    name="ConsignmentNo"
                    value={generatingNumber ? 'Loading...' : formData.ConsignmentNo}
                    onChange={handleChange}
                    required
                    readOnly
                    placeholder={generatingNumber ? "Generating number..." : ""}
                    style={{ 
                      background: generatingNumber ? '#fff3cd' : '#e3f2fd', 
                      fontWeight: 'bold', 
                      fontSize: '1.1rem',
                      color: generatingNumber ? '#856404' : '#000'
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Consignment Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="ConsignmentDate"
                    value={formData.ConsignmentDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Route: Origin & Destination */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title" style={{ background: '#dcfce7', padding: '8px 12px', borderRadius: '6px' }}>
                <MapPin size={18} />
                Consignment Route
              </h3>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Origin (Pickup City) *</label>
                  <select
                    name="OriginCityID"
                    value={formData.OriginCityID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Origin City --</option>
                    {cities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname} ({city.statename})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Destination (Delivery City) *</label>
                  <select
                    name="DestinationCityID"
                    value={formData.DestinationCityID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Destination City --</option>
                    {cities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname} ({city.statename})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Shipper Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <User size={18} />
                Shipper | Consignor
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={() => openCustomerModal('shipper')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={14} />
                  Select Customer
                </button>
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer Code</label>
                  <input
                    type="text"
                    name="ShipperCode"
                    value={formData.ShipperCode}
                    readOnly
                    style={{ background: '#f3f4f6' }}
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="ShipperName"
                    value={formData.ShipperName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ShipperContactPerson"
                    value={formData.ShipperContactPerson}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ShipperPhone"
                    value={formData.ShipperPhone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="text"
                    name="ShipperWhatsApp"
                    value={formData.ShipperWhatsApp}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Flat | Building</label>
                  <input
                    type="text"
                    name="ShipperFlatBuilding"
                    value={formData.ShipperFlatBuilding}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="ShipperStreet"
                    value={formData.ShipperStreet}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Customer ID No.</label>
                  <input
                    type="text"
                    name="ShipperCustomerIDNo"
                    value={formData.ShipperCustomerIDNo}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    name="ShipperCountryID"
                    value={formData.ShipperCountryID}
                    onChange={(e) => handleShipperCountryChange(e.target.value)}
                  >
                    <option value="">-- Select Country --</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryid}>
                        {country.countryname}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    name="ShipperStateID"
                    value={formData.ShipperStateID}
                    onChange={(e) => handleShipperStateChange(e.target.value)}
                    disabled={!formData.ShipperCountryID}
                  >
                    <option value="">-- Select State --</option>
                    {shipperStates.map(state => (
                      <option key={state.stateid} value={state.stateid}>
                        {state.statename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>City</label>
                  <select
                    name="ShipperCityID"
                    value={formData.ShipperCityID}
                    onChange={handleChange}
                    disabled={!formData.ShipperStateID}
                  >
                    <option value="">-- Select City --</option>
                    {shipperCities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Consignee Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <User size={18} />
                Receiver | Consignee
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={() => openCustomerModal('consignee')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={14} />
                  Select Customer
                </button>
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Customer Code</label>
                  <input
                    type="text"
                    name="ConsigneeCode"
                    value={formData.ConsigneeCode}
                    readOnly
                    style={{ background: '#f3f4f6' }}
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="ConsigneeName"
                    value={formData.ConsigneeName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ConsigneeContactPerson"
                    value={formData.ConsigneeContactPerson}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ConsigneePhone"
                    value={formData.ConsigneePhone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="text"
                    name="ConsigneeWhatsApp"
                    value={formData.ConsigneeWhatsApp}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Flat | Building</label>
                  <input
                    type="text"
                    name="ConsigneeFlatBuilding"
                    value={formData.ConsigneeFlatBuilding}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="ConsigneeStreet"
                    value={formData.ConsigneeStreet}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    name="ConsigneeCountryID"
                    value={formData.ConsigneeCountryID}
                    onChange={(e) => handleConsigneeCountryChange(e.target.value)}
                  >
                    <option value="">-- Select Country --</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryid}>
                        {country.countryname}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    name="ConsigneeStateID"
                    value={formData.ConsigneeStateID}
                    onChange={(e) => handleConsigneeStateChange(e.target.value)}
                    disabled={!formData.ConsigneeCountryID}
                  >
                    <option value="">-- Select State --</option>
                    {consigneeStates.map(state => (
                      <option key={state.stateid} value={state.stateid}>
                        {state.statename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>City</label>
                  <select
                    name="ConsigneeCityID"
                    value={formData.ConsigneeCityID}
                    onChange={handleChange}
                    disabled={!formData.ConsigneeStateID}
                  >
                    <option value="">-- Select City --</option>
                    {consigneeCities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Package size={18} />
                Package Details
              </h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="Description"
                    value={formData.Description}
                    onChange={handleChange}
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="Weight"
                    value={formData.Weight}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Pieces</label>
                  <input
                    type="number"
                    name="Pieces"
                    value={formData.Pieces}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Courier Type</label>
                  <input
                    type="text"
                    name="CourierType"
                    value={formData.CourierType}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select
                    name="PaymentMode"
                    value={formData.PaymentMode}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Credit">Credit</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Product Type</label>
                  <input
                    type="text"
                    name="ProductType"
                    value={formData.ProductType}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>No. of Packages</label>
                  <input
                    type="number"
                    name="NoOfPkgs"
                    value={formData.NoOfPkgs}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Volume (m³)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="Volume"
                    value={formData.Volume}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Volume Weight (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="VolumeWeight"
                    value={formData.VolumeWeight}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Invoice Value</label>
                  <input
                    type="number"
                    step="0.01"
                    name="InvoiceValue"
                    value={formData.InvoiceValue}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Transport & Collection */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Truck size={18} />
                Transport & Collection Details
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Despatch Date</label>
                  <input
                    type="date"
                    name="DespatchDate"
                    value={formData.DespatchDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Vehicle</label>
                  <select
                    name="VehicleID"
                    value={formData.VehicleID}
                    onChange={handleChange}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map(veh => (
                      <option key={veh.VehicleID} value={veh.VehicleID}>
                        {veh.RegistrationNo} {veh.VehicleType && `- ${veh.VehicleType}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Driver {drivers.length === 0 && '(No active drivers)'}</label>
                  <select
                    name="DriverID"
                    value={formData.DriverID}
                    onChange={handleChange}
                  >
                    <option value="">Select Driver</option>
                    {drivers.map(driver => (
                      <option key={driver.driverid} value={driver.driverid}>
                        {driver.drivername}
                        {driver.phone && ` - ${driver.phone}`}
                        {driver.licenseno && ` | License: ${driver.licenseno}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Collected By (Employee)</label>
                  <select
                    name="CollectedBy"
                    value={formData.CollectedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Received By (Employee)</label>
                  <select
                    name="ReceivedBy"
                    value={formData.ReceivedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Load Type</label>
                  <select
                    name="Load"
                    value={formData.Load}
                    onChange={handleChange}
                  >
                    <option value="FTL">FTL (Full Truck Load)</option>
                    <option value="LTL">LTL (Less Than Truck Load)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <FileText size={18} />
                Special Instructions
              </h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="NCND"
                      checked={formData.NCND}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    NCND
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="DDCopyBack"
                      checked={formData.DDCopyBack}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    DD Copy Back
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="CashOnly"
                      checked={formData.CashOnly}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    Cash Only
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="ChequeCash"
                      checked={formData.ChequeCash}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    Cheque / Cash
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="CollectMaterial"
                      checked={formData.CollectMaterial}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    Collect Material
                  </label>
                </div>
                <div className="form-group">
                  <label>Material Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    name="MaterialCost"
                    value={formData.MaterialCost}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Reference</label>
                  <input
                    type="text"
                    name="Reference"
                    value={formData.Reference}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <FileText size={18} />
                Delivery Details
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Courier Status</label>
                  <select
                    name="CourierStatusID"
                    value={formData.CourierStatusID}
                    onChange={handleChange}
                  >
                    <option value="">Select Status</option>
                    <option value="1">Pending</option>
                    <option value="2">In Transit</option>
                    <option value="3">Delivered</option>
                    <option value="4">Returned</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Delivery Date</label>
                  <input
                    type="date"
                    name="DeliveryDate"
                    value={formData.DeliveryDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Remarks</label>
                  <textarea
                    name="Remarks"
                    value={formData.Remarks}
                    onChange={handleChange}
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="Transit"
                      checked={formData.Transit}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    Transit
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="SeparateDocs"
                      checked={formData.SeparateDocs}
                      onChange={handleChange}
                      style={{ width: 'auto' }}
                    />
                    Separate Documents
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="form-footer">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            
            {editData && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => printConsignment(formData, CONSIGNMENT_TYPE)}
                disabled={loading}
                style={{
                  background: '#10b981',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Printer size={18} />
                Print Consignment
              </button>
            )}
            
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading || generatingNumber}
            >
              {loading ? (
                <>
                  <div className="btn-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Land Transport Consignment
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="modal-container" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}</h3>
              <button onClick={() => setShowCustomerModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="search-box" style={{ marginBottom: '16px' }}>
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
                style={{ marginBottom: '16px' }}
              >
                <Plus size={16} />
                Add New Customer
              </button>

              {showAddCustomerForm && (
                <div style={{ marginBottom: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '12px' }}>New Customer</h4>
                  <form onSubmit={handleAddCustomer}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label>Name *</label>
                        <input
                          type="text"
                          value={newCustomerData.customername}
                          onChange={(e) => setNewCustomerData({...newCustomerData, customername: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Type</label>
                        <select
                          value={newCustomerData.customertype}
                          onChange={(e) => setNewCustomerData({...newCustomerData, customertype: e.target.value})}
                        >
                          <option value="CR">Credit</option>
                          <option value="CS">Cash</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="text"
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>WhatsApp</label>
                        <input
                          type="text"
                          value={newCustomerData.whatsappno}
                          onChange={(e) => setNewCustomerData({...newCustomerData, whatsappno: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={newCustomerData.email}
                          onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <select
                          value={newCustomerData.countryid}
                          onChange={(e) => handleNewCustomerCountryChange(e.target.value)}
                        >
                          <option value="">Select Country</option>
                          {countries.map(country => (
                            <option key={country.countryid} value={country.countryid}>{country.countryname}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <select
                          value={newCustomerData.stateid}
                          onChange={(e) => handleNewCustomerStateChange(e.target.value)}
                          disabled={!newCustomerData.countryid}
                        >
                          <option value="">Select State</option>
                          {newCustomerStates.map(state => (
                            <option key={state.stateid} value={state.stateid}>{state.statename}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <select
                          value={newCustomerData.cityid}
                          onChange={(e) => setNewCustomerData({...newCustomerData, cityid: e.target.value})}
                          disabled={!newCustomerData.stateid}
                        >
                          <option value="">Select City</option>
                          {newCustomerCities.map(city => (
                            <option key={city.cityid} value={city.cityid}>{city.cityname}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Tax Number</label>
                        <input
                          type="text"
                          value={newCustomerData.taxnumber}
                          onChange={(e) => setNewCustomerData({...newCustomerData, taxnumber: e.target.value})}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button type="submit" className="btn-primary btn-sm">Create & Select</button>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setShowAddCustomerForm(false)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                          No customers found
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map(customer => (
                        <tr key={customer.customerid}>
                          <td>{customer.customercode}</td>
                          <td>{customer.customername}</td>
                          <td>{customer.phone}</td>
                          <td>
                            <div style={{ fontSize: '0.875rem' }}>
                              {customer.cityname && <div>{customer.cityname}</div>}
                              {customer.statename && <div style={{ color: '#666' }}>{customer.statename}</div>}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={() => selectCustomer(customer)}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentEntryFormLT;